import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCamera, FaVideo, FaFilm, FaArrowRight, FaPlay, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const MediaShoot = () => {
  const [showModal, setShowModal] = useState(false);
  const processSliderRef = useRef(null);

  const scrollSlider = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = direction === 'left' ? -350 : 350;
      ref.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const whatsappNumber = '918700186979';
  const whatsappMessage = 'Hello! I am interested in booking a professional photo/video shoot.';
  const openWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`, '_blank');
  };

  const cards = [
    {
      icon: FaCamera,
      title: 'Product Photography',
      description: 'Studio-quality product shoots with professional lighting setup.',
    },
    {
      icon: FaVideo,
      title: 'Brand Video Shoot',
      description: 'Cinematic storytelling videos for ads & social platforms.',
    },
    {
      icon: FaFilm,
      title: 'Reels & Shorts',
      description: 'High-converting short-form content for Instagram & YouTube.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  return (
    <div className="min-h-screen bg-slate-900 overflow-x-hidden">
      <Navbar />

      <div className="relative pt-32 pb-20 min-h-screen flex flex-col justify-center overflow-hidden">
        {/* Full-screen dark cinematic gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a] via-[#111827] to-[#1e293b] z-0" />

        {/* Animated glow blur elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-red-500/15 via-pink-500/10 to-orange-500/15 blur-3xl opacity-60" />
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-red-600/10 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-orange-600/10 rounded-full blur-[120px] animate-pulse delay-1000" />

          {/* Floating elements */}
          <div className="absolute top-20 right-20 w-32 h-32 bg-gradient-to-r from-red-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse duration-[4000ms]" />
          <div className="absolute bottom-20 left-20 w-40 h-40 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-full blur-3xl animate-pulse delay-700 duration-[5000ms]" />
        </div>

        {/* Soft depth effect */}
        <div className="absolute bottom-0 w-full h-32 bg-gradient-to-t from-black/40 to-transparent z-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-col items-center"
          >
            {/* Badge */}
            <motion.div variants={itemVariants} className="mb-8">
              <span className="px-5 py-2 rounded-full bg-white/5 backdrop-blur-md border border-white/10 text-red-400 text-sm font-semibold tracking-wide flex items-center gap-2 shadow-lg shadow-red-500/10">
                <span>🎥</span> Creative Production
              </span>
            </motion.div>

            {/* Main Heading */}
            <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white mb-8 text-center tracking-tight leading-tight max-w-4xl drop-shadow-[0_5px_25px_rgba(255,0,120,0.4)]">
              Professional{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-pink-500 to-orange-500">
                Photo & Video Shoot
              </span>
            </motion.h1>

            {/* Paragraph */}
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-lg sm:text-xl text-gray-300 mb-16 text-center max-w-2xl leading-relaxed font-light"
            >
              High-quality commercial shoots that elevate your brand presence.
              <br className="hidden sm:block" /> From product photography to cinematic brand films.
            </motion.p>

            {/* Service Cards Grid */}
            <motion.div
              variants={containerVariants}
              className="grid md:grid-cols-3 gap-8 w-full mb-16"
            >
              {cards.map((card, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -16, transition: { duration: 0.3 } }}
                  className={`group relative bg-white/8 backdrop-blur-xl border border-white/10 rounded-2xl p-10 transition-all duration-500 ease-out hover:shadow-[0_20px_60px_rgba(236,72,153,0.25)] hover:border-pink-500/40 overflow-hidden animate-[floatCard_6s_ease-in-out_infinite] ${index === 0 ? 'delay-0' : index === 1 ? 'delay-150' : 'delay-300'}`}
                >
                  {/* Subtle animated gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 via-transparent to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative z-10">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white text-2xl mb-6 shadow-lg shadow-red-500/30 group-hover:scale-110 transition-transform duration-300">
                      <card.icon />
                    </div>

                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-red-400 transition-colors">
                      {card.title}
                    </h3>

                    <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">
                      {card.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Button */}
            <motion.div variants={itemVariants}>
              <motion.button
                onClick={openWhatsApp}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="group relative px-10 py-5 bg-gradient-to-r from-red-600 via-red-500 to-orange-600 text-white font-bold rounded-full shadow-2xl shadow-red-500/40 overflow-hidden cursor-pointer inline-flex items-center gap-3 transition-all duration-300 hover:shadow-red-500/60"
              >
                <motion.div
                  animate={{
                    x: [-200, 200],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"
                />
                <span className="relative flex items-center justify-center gap-2 text-lg">
                  Book a Shoot
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                </span>
              </motion.button>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom Curved Wave Design */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-10">
          <svg
            viewBox="0 0 1440 120"
            className="w-full h-20"
            preserveAspectRatio="none"
          >
            <path
              d="M0,64L80,74.7C160,85,320,107,480,106.7C640,107,800,85,960,74.7C1120,64,1280,64,1360,64L1440,64L1440,120L0,120Z"
              fill="#ffffff"
            ></path>
          </svg>
        </div>

        <style jsx>{`
          @keyframes floatCard {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-6px); }
            100% { transform: translateY(0px); }
          }
        `}</style>
      </div>

      {/* SECTION 1: Why Choose Our Production Experience */}
      <section className="relative w-full bg-[#f8fafc] py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Very soft radial glow for airy SaaS feel */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,_rgba(99,102,241,0.05)_0%,_transparent_70%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">Our Production Experience</h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">Years of creative expertise delivering high-impact visual storytelling.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { number: "10+", label: "Years Experience" },
                { number: "500+", label: "Successful Shoots" },
                { number: "120+", label: "Brand Collaborations" },
                { number: "4K / 8K", label: "Cinematic Production" }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1],
                    delay: index * 0.1
                  }}
                  whileHover={{
                    y: -10,
                    scale: 1.02,
                    boxShadow: "0 20px 40px rgba(79, 70, 229, 0.18)",
                    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
                  }}
                  whileTap={{ scale: 0.97 }}
                  className="group p-8 rounded-[18px] bg-white/95 backdrop-blur-sm border border-gray-100 shadow-[0_10px_30px_rgba(0,0,0,0.06)] active:bg-gradient-to-br active:from-indigo-500 active:to-purple-600 transition-all duration-400 ease-out flex flex-col items-center justify-center text-center relative overflow-hidden cursor-pointer"
                >
                  {/* Subtle luxury gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative z-10">
                    <h3 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 mb-2 group-active:text-white group-active:bg-none transition-colors duration-300">{stat.number}</h3>
                    <p className="text-gray-900 font-semibold mb-1 group-active:text-white transition-colors duration-300">{stat.label}</p>
                    <p className="text-gray-500 text-sm font-medium group-active:text-white/80 transition-colors duration-300">Professional Grade</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: AI Generated Sample Showcase */}
      <section className="relative py-28 px-6 bg-gradient-to-br from-[#0f172a] via-[#111827] to-[#1e293b]">
        {/* Top Curved Wave Divider (Transition from white section to dark) */}
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-none z-10 rotate-180">
          <svg
            viewBox="0 0 1440 120"
            className="w-full h-20"
            preserveAspectRatio="none"
          >
            <path
              d="M0,64L80,74.7C160,85,320,107,480,106.7C640,107,800,85,960,74.7C1120,64,1280,64,1360,64L1440,64L1440,120L0,120Z"
              fill="#ffffff"
            ></path>
          </svg>
        </div>

        {/* Subtle Top Border Glow */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl font-extrabold mb-6 text-white">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-500 via-pink-500 to-orange-500">
                  AI Generated Photo & Video Samples
                </span>
              </h2>
              <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto font-light">
                Premium demo visuals crafted for modern brands.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              {/* Image Blocks */}
              {[
                { title: "Cinematic Portrait", img: "https://images.unsplash.com/photo-1516035069371-29a1b244cc32" },
                { title: "Fashion Editorial", img: "https://images.unsplash.com/photo-1492724441997-5dc865305da7" },
              ].map((item, idx) => (
                <div
                  key={`img-${idx}`}
                  className="group relative h-[400px] rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105"
                >
                  <img
                    src={item.img}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 p-8 translate-y-6 group-hover:translate-y-0 transition-transform duration-500">
                    <h3 className="text-white text-2xl font-bold">{item.title}</h3>
                    <div className="h-1 w-12 bg-red-500 mt-3 rounded-full" />
                  </div>
                </div>
              ))}

              {/* Video Blocks */}
              {[
                { title: "Commercial Ad Demo", thumb: "https://images.unsplash.com/photo-1492724441997-5dc865305da7" },
                { title: "Brand Story Demo", thumb: "https://images.unsplash.com/photo-1516035069371-29a1b244cc32" },
              ].map((item, idx) => (
                <div
                  key={`vid-${idx}`}
                  onClick={() => setShowModal(true)}
                  className="group relative h-[400px] rounded-3xl overflow-hidden shadow-xl cursor-pointer border border-white/5 hover:scale-105 transition-all duration-500 hover:shadow-[0_0_40px_rgba(239,68,68,0.3)]"
                >
                  <img
                    src={item.thumb}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700 opacity-80 group-hover:opacity-60"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-500" />

                  {/* Play Button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 shadow-[0_0_30px_rgba(255,255,255,0.1)] group-hover:scale-110 group-hover:bg-red-500/90 group-hover:border-red-500 transition-all duration-300">
                      <FaPlay className="text-white text-2xl ml-1" />
                    </div>
                  </div>

                  <div className="absolute bottom-8 left-8">
                    <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full mb-2 inline-block shadow-lg">VIDEO DEMO</span>
                    <h3 className="text-white text-2xl font-bold drop-shadow-lg">{item.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-5xl aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl border border-white/10"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-6 right-6 z-20 p-3 bg-black/50 hover:bg-red-600 rounded-full text-white transition-all duration-300 backdrop-blur-md border border-white/10 group"
              >
                <FaTimes size={20} className="group-hover:rotate-90 transition-transform duration-300" />
              </button>
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SECTION 3: Behind The Scenes Process */}
      <section className="relative py-24 overflow-hidden bg-[#ffffff]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 px-4">
              <div className="text-center md:text-left mb-8 md:mb-0 w-full">
                <h2 className="text-3xl md:text-5xl font-bold mb-4">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-500 via-pink-500 to-orange-500">
                    Our Creative Production Process
                  </span>
                </h2>
                <p className="text-gray-600 text-lg">
                  From concept to final delivery — crafted with precision.
                </p>
              </div>
            </div>

            {/* Infinite Horizontal Card Slider */}
            <div className="relative w-full overflow-hidden">
              <div className="flex gap-8 animate-scroll hover:[animation-play-state:paused] whitespace-nowrap w-max py-12">
                {[...Array(2)].map((_, i) => (
                  <React.Fragment key={i}>
                    {[
                      { id: "01", title: "Concept Planning", desc: "Strategic storytelling aligned with your brand vision." },
                      { id: "02", title: "Creative Direction", desc: "Mood boards, scripting & visual planning." },
                      { id: "03", title: "Production Shoot", desc: "High-end lighting & cinematic capture." },
                      { id: "04", title: "Editing & Delivery", desc: "Professional color grading & final mastering." },
                    ].map((step, index) => (
                      <motion.div
                        key={`${i}-${index}`}
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          default: { duration: 0.4, ease: "easeOut" },
                          opacity: { duration: 0.8 },
                          y: { duration: 0.8 }
                        }}
                        whileHover={{
                          y: -12,
                          scale: 1.03,
                          boxShadow: "0 25px 60px rgba(255, 80, 120, 0.15)",
                          opacity: 1,
                        }}
                        whileTap={{
                          scale: 0.97,
                          transition: { duration: 0.2 }
                        }}
                        className="group min-w-[320px] bg-white/75 backdrop-blur-md rounded-[28px] p-8 shadow-[0_15px_40px_rgba(0,0,0,0.08)] flex flex-col justify-between h-[340px] whitespace-normal opacity-85 cursor-pointer border border-white/50"
                      >
                        <div>
                          <span className="text-6xl font-black text-gray-100/80 mb-6 block transition-colors group-hover:text-pink-500/10">{step.id}</span>
                          <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                          <p className="text-gray-500 leading-relaxed">{step.desc}</p>
                        </div>
                        <div className="w-12 h-1 rounded-full bg-gradient-to-r from-red-500 to-pink-500 mt-6 transition-all duration-500 group-hover:w-20" />
                      </motion.div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <style jsx>{`
              @keyframes scrollX {
                0% { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
              .animate-scroll {
                animation: scrollX 22s linear infinite;
              }
            `}</style>
          </motion.div>
        </div>

        {/* Bottom Curved Wave Divider (Transition from process section to white CTA) */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-10">
          <svg
            viewBox="0 0 1440 120"
            className="w-full h-20"
            preserveAspectRatio="none"
          >
            <path
              d="M0,64L80,74.7C160,85,320,107,480,106.7C640,107,800,85,960,74.7C1120,64,1280,64,1360,64L1440,64L1440,120L0,120Z"
              fill="#ffffff"
            ></path>
          </svg>
        </div>
      </section>

      {/* SECTION 5: Premium Call-To-Action Strip */}
      <section className="py-20 bg-white">
        <motion.div
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-7xl mx-auto px-6 md:px-8"
        >
          <div className="bg-gradient-to-r from-red-500 via-pink-500 to-orange-500 rounded-3xl py-16 px-6 md:px-20 text-center text-white shadow-2xl shadow-red-500/20 relative overflow-hidden group">
            {/* Subtle Glow Overlay */}
            <div className="absolute inset-0 bg-white/10 blur-3xl opacity-0 group-hover:opacity-30 transition-opacity duration-700" />

            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">Ready to Elevate Your Brand Visually?</h2>
              <p className="text-white/90 text-lg md:text-xl mb-10 max-w-2xl mx-auto font-medium">
                Let’s create cinematic visuals that convert.
              </p>

              <button
                onClick={openWhatsApp}
                className="bg-white text-red-600 font-bold rounded-full px-10 py-4 text-lg hover:scale-105 transition-transform duration-300 shadow-xl hover:shadow-2xl inline-flex items-center gap-2"
              >
                Book a Shoot
                <FaArrowRight className="text-red-500" />
              </button>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default MediaShoot;
